import { Alert, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { useState, type FormEvent } from 'react';
import { Navigate, Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';

import type { SessionKind } from '../api/SessionKind';
import { homeOf } from '../auth/home';
import { useSession } from '../auth/SessionContext';
import { audienceOfKind, kindOfAudience } from '../components/audience';
import { KindToggle } from '../components/KindToggle';
import { PublicPage } from '../components/PublicPage';
import { publicErrorMessage } from '../components/publicErrorMessage';
import { RevealablePasswordField } from '../components/RevealablePasswordField';
import { useT } from '../i18n/LanguageContext';

/**
 * One sign-in for both audiences. The toggle decides which API is asked — business-api by default, because
 * that is who signs in every day; a super admin flips it once (ADR-0002). `?audience=admin` presets it, so a
 * link back from a password page lands on the right side.
 */
export const LoginPage = () => {
  const { profile, signIn } = useSession();
  const navigate = useNavigate();
  const t = useT();
  const [searchParams] = useSearchParams();
  const [kind, setKind] = useState<SessionKind>(kindOfAudience(searchParams.get('audience')));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  if (profile) {
    return <Navigate to={homeOf(profile.kind)} replace />;
  }

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setIsBusy(true);

    try {
      await signIn(kind, email, password);
      navigate(homeOf(kind));
    } catch (caught) {
      setError(publicErrorMessage(caught, t));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <PublicPage>
      <KindToggle value={kind} onChange={setKind} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.55 }}>
        {kind === 'business' ? t('login.hint.business') : t('login.hint.platform')}
      </Typography>

      <form onSubmit={submit}>
        <Stack spacing={2}>
          {error ? (
            <Alert severity="error" variant="outlined">
              {error}
            </Alert>
          ) : null}
          <TextField
            label={t('field.email')}
            type="email"
            size="medium"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
            autoFocus
            required
            fullWidth
          />
          <RevealablePasswordField
            label={t('field.password')}
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
          />
          <Button type="submit" variant="contained" size="large" disabled={isBusy}>
            {isBusy ? t('login.busy') : t('login.submit')}
          </Button>
          <Link
            component={RouterLink}
            to={`/password/forgot?audience=${audienceOfKind(kind)}`}
            variant="body2"
          >
            {t('login.forgot')}
          </Link>
        </Stack>
      </form>
    </PublicPage>
  );
};
